for (const film of document.querySelectorAll('[data-pdp-film]')) {
  const video = film.querySelector('[data-pdp-film-player]');
  const start = film.querySelector('[data-pdp-film-start]');
  const error = film.querySelector('[data-pdp-film-error]');
  const retry = film.querySelector('[data-pdp-film-retry]');
  const item = film.closest('[data-gallery-item]');
  if (!video || !start || !error || !retry || !item) continue;

  const WATCHDOG_MS = 7000;
  let visible = false;
  let request = 0;
  let pending = false;
  let watchdog;
  let lastProgress = 0;
  let autoBlocked = false;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const canAutoPlay = () => !motion.matches && !connection?.saveData && !document.hidden && !item.hidden && visible;
  const clearWatchdog = () => {
    clearTimeout(watchdog);
    watchdog = undefined;
  };
  const cancelAttempt = () => {
    request++;
    pending = false;
    clearWatchdog();
    film.setAttribute('aria-busy', 'false');
  };
  const clearError = () => {
    error.hidden = true;
    film.dataset.error = 'false';
    delete film.dataset.failureKind;
  };
  const pausedState = () => {
    cancelAttempt();
    film.dataset.playing = 'false';
    start.hidden = !error.hidden;
  };
  const pause = () => {
    cancelAttempt();
    video.pause();
    film.dataset.playing = 'false';
    start.hidden = !error.hidden;
  };
  const fail = kind => {
    cancelAttempt();
    autoBlocked = true;
    video.pause();
    film.dataset.playing = 'false';
    film.dataset.error = 'true';
    film.dataset.failureKind = kind;
    start.hidden = true;
    error.hidden = false;
  };
  const resetWatchdog = () => {
    clearWatchdog();
    const current = request;
    watchdog = setTimeout(() => {
      watchdog = undefined;
      if (current === request && !document.hidden && !item.hidden) fail('timeout');
    }, WATCHDOG_MS);
  };
  const armWatchdog = () => {
    if (watchdog !== undefined) return;
    film.setAttribute('aria-busy', 'true');
    resetWatchdog();
  };
  const play = async (manual = false) => {
    if (!manual && (!canAutoPlay() || autoBlocked || !error.hidden)) return;
    if (pending || !video.paused) return;
    if (manual) autoBlocked = false;
    clearError();
    const current = ++request;
    pending = true;
    lastProgress = video.currentTime;
    video.muted = true;
    video.preload = 'metadata';
    armWatchdog();
    try {
      await video.play();
      if (current !== request) return;
      pending = false;
      clearWatchdog();
      film.setAttribute('aria-busy', 'false');
      film.dataset.playing = 'true';
      start.hidden = true;
    } catch (cause) {
      if (current !== request) return;
      if (cause?.name === 'NotAllowedError' && !manual) {
        autoBlocked = true;
        pause();
        film.dataset.failureKind = 'autoplay-policy';
      } else if (cause?.name === 'AbortError' && (document.hidden || item.hidden)) {
        pause();
      } else {
        fail(cause?.name === 'NotAllowedError' ? 'playback-policy' : 'network');
      }
    }
  };

  start.addEventListener('click', () => play(true));
  retry.addEventListener('click', () => {
    if (pending) return;
    cancelAttempt();
    clearError();
    start.hidden = true;
    video.load();
    play(true);
  });
  video.addEventListener('playing', () => {
    if (document.hidden || item.hidden) { pause(); return; }
    if (!error.hidden) { video.pause(); return; }
    pending = false;
    clearWatchdog();
    clearError();
    film.setAttribute('aria-busy', 'false');
    film.dataset.playing = 'true';
    start.hidden = true;
  });
  video.addEventListener('loadedmetadata', () => {
    const frame = film.closest('.gallery-active-frame');
    if (frame?.hasAttribute('data-gallery-frame-locked')) return;
    if (video.videoWidth > 0 && video.videoHeight > 0) {
      frame?.style.setProperty('--gallery-media-ratio', `${video.videoWidth} / ${video.videoHeight}`);
    }
  });
  video.addEventListener('waiting', () => {
    if (!video.paused && error.hidden) armWatchdog();
  });
  video.addEventListener('stalled', () => {
    if (!video.paused && error.hidden) armWatchdog();
  });
  video.addEventListener('timeupdate', () => {
    if (watchdog === undefined || pending || video.paused || !error.hidden || document.hidden || item.hidden) return;
    if (Math.abs(video.currentTime - lastProgress) <= 0.05) return;
    lastProgress = video.currentTime;
    resetWatchdog();
    film.setAttribute('aria-busy', 'false');
  });
  video.addEventListener('pause', pausedState);
  video.addEventListener('ended', pausedState);
  video.addEventListener('error', () => {
    if (!document.hidden && !item.hidden && (pending || !video.paused)) fail('network');
  });
  const observer = new IntersectionObserver(entries => {
    visible = entries[0]?.isIntersecting === true;
    if (canAutoPlay()) play();
    else if (!visible) pause();
  }, {threshold: 0.35});
  observer.observe(item);
  const selection = new MutationObserver(() => {
    if (item.hidden) pause();
    else if (canAutoPlay()) play();
  });
  selection.observe(item, {attributes:true, attributeFilter:['hidden']});
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) pause();
    else if (canAutoPlay()) play();
  });
  motion.addEventListener?.('change', () => {
    if (motion.matches) pause();
    else if (canAutoPlay()) play();
  });
}
